﻿import { ContentDeliverable, CourseType, DeliverableType, License, MostRestrictiveLicense, TransactionDetails } from 'apis/TransactionApi';
import { NonAvailableLicense, PendingLicenseRestriction, SelectedContent, SelectedContentReference } from 'apis/TransactionApi';
import { ContentTiedToExpiredOrder } from 'bundles/accessCodesDefault/components/TransactionProfile/TransactionProfile';
import { compareArray, filterOutNonExistentOrderIds } from 'utils/arrays';
import { LicenseType } from 'types';

export function values<T>(obj: T): T[keyof T][] {

    return Object.values(obj as any);

}

export const unlimitedUses = 9999;

// Check against uses if -1(null) that means there's no deliverable in the license.
// If Site license check max uses, if max uses has a number, use uses if it's -1(null) then it's unlimited.
// If Individual license just return uses.
export const getLowestLicenseAmount = (contentLicense: License) => {

    // Finding the deliverable type that has the lowest uses count.
    const usesToCheckAgainst: number[] = [];
    if (contentLicense.LicenseType === LicenseType.Individual) {

        if (contentLicense.PracticeTestUses !== -1) {

            usesToCheckAgainst.push(contentLicense.PracticeTestUses);

        }
        if (contentLicense.CourseUses !== -1) {

            usesToCheckAgainst.push(contentLicense.CourseUses);

        }
        if (contentLicense.EbookUses !== -1) {

            usesToCheckAgainst.push(contentLicense.EbookUses);

        }
        if (contentLicense.XEDUses !== -1) {

            usesToCheckAgainst.push(contentLicense.XEDUses);

        }

    } else {

        if (contentLicense.PracticeTestUses !== -1 && contentLicense.LicensePracticeTestsMaxUses !== -1) {

            usesToCheckAgainst.push(contentLicense.PracticeTestUses);

        }
        if (contentLicense.CourseUses !== -1 && contentLicense.LicenseCoursesMaxUses !== -1) {

            usesToCheckAgainst.push(contentLicense.CourseUses);

        }
        if (contentLicense.EbookUses !== -1 && contentLicense.LicenseEbookMaxUses !== -1) {

            usesToCheckAgainst.push(contentLicense.EbookUses);

        }
        if (contentLicense.XEDUses !== -1 && contentLicense.LicenseXEDMaxUses !== -1) {

            usesToCheckAgainst.push(contentLicense.XEDUses);

        }

    }
    const minDeliverableCount = Math.min(...usesToCheckAgainst);
    if (minDeliverableCount === Infinity || minDeliverableCount === -1) {

        // if (minDeliverableCount === -1) console.error("ERROR: SOMETHING IS WRONG getLowestLicenseAmount");
        return unlimitedUses;

    } else {

        return minDeliverableCount;

    }

};

export const getMaxLicenseAmount = (contentLicense: License) => {

    // Finding the deliverable type that has the lowest uses count.
    if (contentLicense.LicenseType === LicenseType.Individual) {

        return contentLicense.NumGranted;

    } else {

        const usesToCheckAgainst: number[] = [];
        if (contentLicense.PracticeTestUses !== -1) {

            usesToCheckAgainst.push(contentLicense.LicensePracticeTestsMaxUses);

        }
        if (contentLicense.CourseUses !== -1) {

            usesToCheckAgainst.push(contentLicense.LicenseCoursesMaxUses);

        }
        if (contentLicense.EbookUses !== -1) {

            usesToCheckAgainst.push(contentLicense.LicenseEbookMaxUses);

        }

        const minDeliverableCount = Math.min(...usesToCheckAgainst);
        if (minDeliverableCount === Infinity || minDeliverableCount === -1) {

            // if (minDeliverableCount === -1) console.error("ERROR: SOMETHING IS WRONG getLowestLicenseAmount");
            return unlimitedUses;

        } else {

            return minDeliverableCount;

        }

    }

};

// Returns true if the partner has at least one license that has content (a non-empty deliverable)
// AND has available uses (either unlimited or a positive remaining count).
export const hasUsableLicense = (licenses: License[]) => licenses.some(license => {

    // Each deliverable pairs its remaining-uses pool with its per-access-code max-uses.
    const deliverables: { maxUses: number; uses: number }[] = [
        { maxUses: license.LicensePracticeTestsMaxUses, uses: license.PracticeTestUses },
        { maxUses: license.LicenseCoursesMaxUses, uses: license.CourseUses },
        { maxUses: license.LicenseEbookMaxUses, uses: license.EbookUses },
        { maxUses: license.LicenseXEDMaxUses, uses: license.XEDUses }
    ];

    const isSiteLicense = license.LicenseType === LicenseType.Site;

    return deliverables.some(({ maxUses, uses }) => {

        // -1 means the deliverable is not present on this license.
        if (uses === -1) return false;

        // For site licenses a per-code max of -1 means unlimited uses, so the
        // deliverable is usable even when the remaining pool reads 0.
        if (isSiteLicense && maxUses === -1) return true;

        // Otherwise the deliverable is only usable when it still has remaining uses.
        return uses > 0;

    });

});

export const getIndividualLowestLicenseAmount = (contentLicense: License) => {

    // Finding the deliverable type that has the lowest uses count.
    const usesToCheckAgainst: number[] = [];
    if (contentLicense.LicensePracticeTestsNumUses > 1) {

        usesToCheckAgainst.push(contentLicense.PracticeTestUses);

    }
    if (contentLicense.LicenseCoursesNumUses > 1) {

        usesToCheckAgainst.push(contentLicense.CourseUses);

    }
    if (contentLicense.LicenseEbookNumUses > 1) {

        usesToCheckAgainst.push(contentLicense.EbookUses);

    }
    const minDeliverableCount = Math.min(...usesToCheckAgainst);
    if (minDeliverableCount === Infinity) {

        return unlimitedUses;

    } else {

        return minDeliverableCount;

    }

};

export const getTotalLicenseUses = (contentLicense: License) => {

    const usesToCheckAgainst: number[] = [];
    if (contentLicense === undefined) {

        return;

    }
    if (contentLicense.LicenseType === LicenseType.Individual) {

        return contentLicense.NumGranted;

    } else {

        if (contentLicense.LicensePracticeTestsMaxUses > 1) {

            usesToCheckAgainst.push(contentLicense.LicensePracticeTestsMaxUses);

        }
        if (contentLicense.LicenseCoursesMaxUses > 1) {

            usesToCheckAgainst.push(contentLicense.LicenseCoursesMaxUses);

        }
        if (contentLicense.LicenseEbookMaxUses > 1) {

            usesToCheckAgainst.push(contentLicense.LicenseEbookMaxUses);

        }
        if (contentLicense.LicenseXEDMaxUses > 1) {

            usesToCheckAgainst.push(contentLicense.LicenseXEDMaxUses);

        }
        const minDeliverableCount = Math.min(...usesToCheckAgainst);
        if (minDeliverableCount === Infinity) {

            return unlimitedUses;

        } else {

            return minDeliverableCount * contentLicense.NumGranted;

        }

    }

};

// getLowestLicenseAmount needs to account for the content that's on the transaction.
//example - transaction with only courses but is using an order that gives practiceTestUses, courseUses etc. The practiceTestUses could be restricting the transaction instead of the courseUses.
export const getMostRestrictedLicenseDeliverable = (
    licenses: License[],
    content: ContentDeliverable[],
    xedEnabled = false
): MostRestrictiveLicense => {

    let license = licenses[0];
    let avaliableStudentsAllowed = -1; // means unlimited for this variable.

    if (content.length > 0 || xedEnabled === false) {

        license = licenses[0];
        content.map(content => {

            if (licenses) {

                const contentLicense = licenses.find(
                    (license: License) => license.OrderId === content.RestrictedOrderId
                );
                if (contentLicense) {

                    if (license === null) {

                        license = contentLicense;

                    } else {

                        license = contentLicense;
                        avaliableStudentsAllowed = getLowestLicenseAmount(contentLicense);

                    }

                }

            }

        });

    }

    // check if XED is enabled and then check if the XED license is more restrictive than the one picked previously.
    // if so, update the XED properties and available students before returning the license.
    if (xedEnabled === true) {

        const xedLicense = getXedLicenseWithLatestExpiration(licenses);

        if (xedLicense) {

            // if we have no other content, update the license to the XED license
            if (content.length === 0) {

                license = xedLicense.license;
                avaliableStudentsAllowed = xedLicense.avaliableStudentsAllowed;

            }
            if (xedLicense?.avaliableStudentsAllowed < avaliableStudentsAllowed) {

                avaliableStudentsAllowed = xedLicense.avaliableStudentsAllowed;
                license.XEDUses = xedLicense.license.XEDUses;
                license.LicenseXEDNumUses = xedLicense.license.LicenseXEDNumUses;
                license.LicenseXEDMaxUses = xedLicense.license.LicenseXEDMaxUses;

            }

        }

    }

    return { avaliableStudentsAllowed, license };

};

export const getXedLicenseWithLatestExpiration = (licenses: License[]): MostRestrictiveLicense | undefined => {

    let license: License | undefined = undefined;

    const xedLicenses = licenses.filter(x => x.LicenseXEDNumUses > 0);

    for (let i = 0; i < xedLicenses.length; i++) {

        if (!license || xedLicenses[i].OrderExpireDate > license.OrderExpireDate) {

            license = xedLicenses[i];

        }

    }

    if (license === undefined) return undefined;

    // calculate num uses remaining
    let numUses = license.XEDUses;

    // if this is an unlimited license, return 999
    if (license.LicenseXEDMaxUses === -1 && license.XEDUses === 1) {

        numUses = 999;

    }

    return { avaliableStudentsAllowed: numUses, license };

};

export const getCloudLicenseWithLatestExpiration = (licenses: License[]): License | undefined => {

    const cloudLicenses = licenses.filter(x => x.CloudAccessUses > 0);
    if (cloudLicenses.length > 0) {

        let license: License | undefined = cloudLicenses[0];

        for (let i = 0; i < cloudLicenses.length; i++) {

            if (license && cloudLicenses[i].OrderExpireDate > license.OrderExpireDate) {

                license = cloudLicenses[i];

            }

        }

        if (license === undefined) return undefined;

        return license;

    } else {

        return undefined;

    }

};

export const handlePendingContent = (
    transactionDetails: TransactionDetails,
    currentlySelected: ContentDeliverable[],
    content: ContentDeliverable[],
    licenses: License[],
    pendingLicenseRestrictions: PendingLicenseRestriction[],
    selectedPendingLicenseRestrictions: License[][] = []
): { pendingLicenseRestrictions: PendingLicenseRestriction[]; success: boolean; transaction: TransactionDetails } => {

    const pendingTests: SelectedContentReference[] = [];
    const pendingBooks: SelectedContentReference[] = [];
    const pendingCourses: SelectedContentReference[] = [];
    let orderIds: number[] = transactionDetails?.OrderIds ? [...new Set(transactionDetails?.OrderIds)] : [];
    let listOfPendingLicenseRestrictions: PendingLicenseRestriction[] = [];
    let bookCount = 0;
    let courseCount = 0;
    let selected = [...currentlySelected];
    let XEDCourseAdded = transactionDetails?.NumXEDAccess ?? 0;

    if (currentlySelected.length > 0) {

        selected = selected.map(checkedPieceOfContent => {

            if (!checkedPieceOfContent.OrderIds) {

                const getOrderIdsPieceOfContent = content.find(
                    pieceOfContent => pieceOfContent.Id === checkedPieceOfContent.Id &&
                        pieceOfContent.DeliverableTypeId === checkedPieceOfContent.DeliverableTypeId
                );
                if (getOrderIdsPieceOfContent) {

                    checkedPieceOfContent.OrderIds = getOrderIdsPieceOfContent.OrderIds;

                }

            }
            return checkedPieceOfContent;

        });

    }

    if (transactionDetails?.EBookIds) {

        bookCount = transactionDetails.EBookIds.length;

    }
    if (transactionDetails?.CourseIds) {

        courseCount = transactionDetails.CourseIds.length;

    }

    for (let i = 0; i < selected.length; i++) {

        const selectedItem = selected[i];
        const selectedItemReference: SelectedContent = {
            DeliverableTypeId: selectedItem.DeliverableTypeId,
            Id: selectedItem.Id,
            Name: selectedItem.Name,
            OrderIds: filterOutNonExistentOrderIds(selectedItem.OrderIds, licenses)
        };

        // Pushing into different arrays by deliverableTypeId for AddContentToTransaction.
        switch (true) {

            case selectedItem.DeliverableTypeId === DeliverableType.Test && selectedItem.PendingAdd:
                if (selectedItem.PendingAdd && selectedItem.OrderIds.length === 1) {

                    pendingTests.push({
                        id: selectedItem.Id,
                        restrictedOrderId: selectedItem.OrderIds[0]
                    });
                    orderIds.push(selectedItem.OrderIds[0]);

                } else if (selectedItem.PendingAdd && selectedItem.OrderIds.length > 1) {

                    handlePendingLicenseRestrictions(selectedItemReference, listOfPendingLicenseRestrictions, licenses);

                }
                break;
            case selectedItem.DeliverableTypeId === DeliverableType.Book && selectedItem.PendingAdd:
                bookCount++;
                if (selectedItem.PendingAdd && selectedItem.OrderIds.length === 1) {

                    pendingBooks.push({ id: selectedItem.Id, restrictedOrderId: selectedItem.OrderIds[0] });
                    orderIds.push(selectedItem.OrderIds[0]);

                } else if (selectedItem.PendingAdd && selectedItem.OrderIds.length > 1) {

                    handlePendingLicenseRestrictions(selectedItemReference, listOfPendingLicenseRestrictions, licenses);

                }
                break;
            case selectedItem.DeliverableTypeId === DeliverableType.Course && selectedItem.PendingAdd:
                courseCount++;
                if (selectedItem.CourseType === CourseType.XEDCourse && XEDCourseAdded !== 1) {

                    XEDCourseAdded = 1;

                }
                if (selectedItem.PendingAdd && selectedItem.OrderIds.length === 1) {

                    pendingCourses.push({
                        id: selectedItem.Id,
                        restrictedOrderId: selectedItem.OrderIds[0]
                    });
                    orderIds.push(selectedItem.OrderIds[0]);

                } else if (selectedItem.PendingAdd && selectedItem.OrderIds.length > 1) {

                    handlePendingLicenseRestrictions(selectedItemReference, listOfPendingLicenseRestrictions, licenses);

                }
                break;

        }

    }

    if (listOfPendingLicenseRestrictions.length !== pendingLicenseRestrictions.length) {

        // setPendingLicenseRestrictions(listOfPendingLicenseRestrictions);
        // setSelectedPendingLicenseRestrictions(listOfPendingLicenseRestrictions.map(() => []));
        return {
            pendingLicenseRestrictions: listOfPendingLicenseRestrictions,
            success: false,
            transaction: transactionDetails
        };

    }

    const validPendingLicenseRestrictions = [...listOfPendingLicenseRestrictions];
    let validPending = true;
    if (validPendingLicenseRestrictions.length > 0) {

        for (let i = 0; i < selectedPendingLicenseRestrictions.length; i++) {

            if (selectedPendingLicenseRestrictions[i].length === 0) {

                validPending = false;
                validPendingLicenseRestrictions[i].Valid = false;

            } else {

                validPendingLicenseRestrictions[i].Valid = true;

            }

        }

    }
    if (!validPending) {

        // setPendingLicenseRestrictions(validPendingLicenseRestrictions);
        return {
            pendingLicenseRestrictions: validPendingLicenseRestrictions,
            success: false,
            transaction: transactionDetails
        };

    }

    const orderIdsToAdd = selectedPendingLicenseRestrictions
        .flat()
        .map(licenseRestriction => licenseRestriction.OrderId);

    insertPendedLicenseRestrictionDeliverables(
        orderIdsToAdd,
        validPendingLicenseRestrictions,
        pendingTests,
        pendingBooks,
        pendingCourses
    );
    orderIds = [...new Set([...orderIds, ...orderIdsToAdd])];
    listOfPendingLicenseRestrictions = [];

    if (transactionDetails) {

        let newExpirationDate: Date | undefined = undefined;
        if (transactionDetails.ExpirationDate === undefined) {

            [...new Set(orderIds)].forEach(orderId => {

                const license = licenses.find(license => license.OrderId === orderId);
                if (newExpirationDate) {

                    if (license && new Date(license.OrderExpireDate) < new Date(newExpirationDate)) {

                        newExpirationDate = new Date(license.OrderExpireDate);

                    }

                } else {

                    if (license) {

                        newExpirationDate = new Date(license.OrderExpireDate);

                    }

                }

            });

        }

    }

    return {
        pendingLicenseRestrictions: [],
        success: true,
        transaction: {
            ...transactionDetails,
            NumOfBooks: bookCount,
            NumOfCourses: courseCount,
            NumOfTests:
                pendingTests.length > 0 &&
                    (transactionDetails.NumOfTests === 0 || transactionDetails.NumOfTests === undefined)
                    ? 100
                    : transactionDetails.NumOfTests,
            NumXEDAccess: XEDCourseAdded,
            OrderIds: [...new Set(orderIds)],
            PendingBooks: pendingBooks,
            PendingCourses: pendingCourses,
            PendingTests: pendingTests
        }
    };

};

const insertPendedLicenseRestrictionDeliverables = (
    orderIdsToAdd: number[],
    validPendingLicenseRestrictions: PendingLicenseRestriction[],
    pendingTests: SelectedContentReference[],
    pendingBooks: SelectedContentReference[],
    pendingCourses: SelectedContentReference[]
) => {

    validPendingLicenseRestrictions.map((validPendingLicenseRestriction, i) => {

        validPendingLicenseRestriction.ConflictingDeliverableList.map(contentLevelObj => {

            switch (true) {

                case contentLevelObj.DeliverableTypeId === DeliverableType.Test:
                    pendingTests.push({ id: contentLevelObj.Id, restrictedOrderId: orderIdsToAdd[i] });
                    break;
                case contentLevelObj.DeliverableTypeId === DeliverableType.Book:
                    pendingBooks.push({ id: contentLevelObj.Id, restrictedOrderId: orderIdsToAdd[i] });
                    break;
                case contentLevelObj.DeliverableTypeId === DeliverableType.Course:
                    pendingCourses.push({ id: contentLevelObj.Id, restrictedOrderId: orderIdsToAdd[i] });
                    break;

            }

        });

    });

};

export const handlePendingLicenseRestrictions = (
    selectedItem: SelectedContent,
    listOfPendingLicenseRestrictions: PendingLicenseRestriction[],
    licenses: License[]
) => {

    const pendingLicenseRestrictionToAdd: PendingLicenseRestriction = {
        ConflictingDeliverableList: [],
        LicenseRestrictions: [],
        NonAvailableLicenseList: [],
        OrderIds: selectedItem.OrderIds,
        Valid: true
    };
    const nonAvailableLicenseList: NonAvailableLicense[] = [];

    // if (allowedLicenses) {
    // 	selectedItem.OrderIds = removeOrdersWithLessUsesThanCurrentTransactionStudents(
    // 		selectedItem,
    // 		allowedLicenses,
    // 		pageContext.Data.mostRestrictiveLicense,
    // 		nonAvailableLicenseList
    // 	);
    // }

    let alreadyExistsPendingLicenseRestriction = false;
    listOfPendingLicenseRestrictions.map(pendingLicenseRestriction => {

        if (compareArray(pendingLicenseRestriction.OrderIds, [...new Set(selectedItem.OrderIds)])) {

            alreadyExistsPendingLicenseRestriction = true;

        }

    });

    if (!alreadyExistsPendingLicenseRestriction) {

        selectedItem.OrderIds.map(selectedOrderId => {

            const licenseRestriction = licenses.find(restriction => restriction.OrderId === selectedOrderId);

            if (licenseRestriction) {

                pendingLicenseRestrictionToAdd.LicenseRestrictions.push(licenseRestriction);

            }

        });
        pendingLicenseRestrictionToAdd.ConflictingDeliverableList.push(selectedItem);
        pendingLicenseRestrictionToAdd.NonAvailableLicenseList = [
            ...pendingLicenseRestrictionToAdd.NonAvailableLicenseList,
            ...nonAvailableLicenseList
        ];
        listOfPendingLicenseRestrictions.push(pendingLicenseRestrictionToAdd);

    } else {

        listOfPendingLicenseRestrictions[listOfPendingLicenseRestrictions.length - 1].ConflictingDeliverableList.push(
            selectedItem
        );
        listOfPendingLicenseRestrictions[listOfPendingLicenseRestrictions.length - 1].NonAvailableLicenseList = [
            ...listOfPendingLicenseRestrictions[listOfPendingLicenseRestrictions.length - 1].NonAvailableLicenseList,
            ...nonAvailableLicenseList
        ];

    }

};

export const chunkArray = (array: any[], chunkSize: number) => {

    const result = [];
    for (let i = 0; i < array.length; i += chunkSize) {

        result.push(array.slice(i, i + chunkSize));

    }
    return result;

};

export const removeOrdersWithLessUsesThanCurrentTransactionStudents = (
    content: SelectedContent,
    allLicenseRestrictions: License[],
    mostRestrictiveLicense: {
        avaliableStudentsAllowed: number;
        license: License;
    },
    nonAvailableLicenseList: NonAvailableLicense[],
    existingTransactionOrderIds: number[] = []
) => {

    const updatedContentOrderIds = content.OrderIds;
    content.OrderIds.map((orderId, i) => {

        const license = allLicenseRestrictions.find(license => license.OrderId === orderId);
        if (license) {

            const studentUsesCount = getLowestLicenseAmount(license);
            const orderAlreadyOnTransaction = existingTransactionOrderIds.includes(orderId);
            if (studentUsesCount !== -1 && studentUsesCount < mostRestrictiveLicense.avaliableStudentsAllowed && !orderAlreadyOnTransaction) {

                nonAvailableLicenseList.push({ content, license });
                updatedContentOrderIds.splice(i, 1);

            }

        }

    });
    return updatedContentOrderIds;

};

export const contentRestrictedByInactiveOrders = (
    content: ContentDeliverable[],
    inactiveOrders: License[]
): ContentTiedToExpiredOrder[] => {

    const contentRestrictedByOrders: ContentTiedToExpiredOrder[] = [];
    let contentDoesntHaveRestrictedOrderId = false;

    for (let i = 0; i < content.length; i++) {

        const pieceOfContent = content[i];
        const pieceOfContentTiedToInactiveOrder = inactiveOrders.find(
            order => order.OrderId === pieceOfContent.RestrictedOrderId
        );
        if (pieceOfContent.RestrictedOrderId === 0) {

            contentDoesntHaveRestrictedOrderId = true;
            break;

        }
        if (pieceOfContentTiedToInactiveOrder) {

            const orderAlreadyRestrictedIndex = contentRestrictedByOrders.findIndex(
                order => order.expiredOrder.Id === pieceOfContentTiedToInactiveOrder.Id
            );
            if (orderAlreadyRestrictedIndex !== -1) {

                contentRestrictedByOrders[orderAlreadyRestrictedIndex].content.push(pieceOfContent);

            } else {

                contentRestrictedByOrders.push({
                    content: [pieceOfContent],
                    expiredOrder: pieceOfContentTiedToInactiveOrder
                });

            }

        }

    }

    if (contentDoesntHaveRestrictedOrderId) {

        contentRestrictedByOrders.push({
            content: [...content],
            expiredOrder: inactiveOrders[0]
        });

    }

    return contentRestrictedByOrders;

};

// If all the pending content that they're adding is part of an order that's already active on the transaction, then you can add the content.
export const pendingContentIsPartOfCurrentOrderOnTransaction = (currentOrderIds: number[], pendingContent: ContentDeliverable[] | undefined) => {

    let pendingContentIsPartOfOrderOnTransaction = true;
    if (pendingContent) {

        pendingContent.forEach(pieceOfContent => {

            if (!currentOrderIds?.includes(pieceOfContent.OrderIds[0])) {

                pendingContentIsPartOfOrderOnTransaction = false;

            }

        });

        return pendingContentIsPartOfOrderOnTransaction;

    } else {

        return false;

    }

};
